// Purpose: Initialize Firebase Remote Config and provide feature flag utilities.
// Remote Config allows percentage-based rollouts of features without redeploying the frontend.
// Flags are configured in the Firebase Console under Remote Config.

import {
  getRemoteConfig,
  fetchAndActivate,
  getValue,
  type RemoteConfig,
} from 'firebase/remote-config';
import { app } from '@/features/auth/config/firebase-config';
import { logger } from '@/features/help/utils/logger';

let remoteConfig: RemoteConfig | null = null;
let isInitialized = false;

// Purpose: Get or create the Remote Config instance
const getRemoteConfigInstance = (): RemoteConfig => {
  if (!remoteConfig) {
    remoteConfig = getRemoteConfig(app);

    // Set minimum fetch interval based on environment
    // In development, use a shorter interval for faster testing
    // In production, use 1 hour to reduce API calls
    const isDev = import.meta.env.VITE_ENV === 'development';
    remoteConfig.settings.minimumFetchIntervalMillis = isDev
      ? 60000 // 1 minute in development
      : 3600000; // 1 hour in production

    // Default values used before Remote Config is fetched or if fetch fails
    // Add new feature flags here with their default (safe) values
    remoteConfig.defaultConfig = {
      // Example feature flags - add your own here
      // new_dashboard_enabled: false,
      // new_field_mapping_v2: false,
    };

    logger.info(
      'FeatureFlags',
      'Remote Config initialized with fetch interval',
      {
        minimumFetchIntervalMillis:
          remoteConfig.settings.minimumFetchIntervalMillis,
      }
    );
  }

  return remoteConfig;
};

// Purpose: Initialize feature flags by fetching and activating Remote Config.
// Should be called once on app startup (e.g., in main.tsx or App.tsx).
// Returns true if initialization was successful, false otherwise.
export const initFeatureFlags = async (): Promise<boolean> => {
  if (isInitialized) {
    logger.debug('FeatureFlags', 'Already initialized, skipping');
    return true;
  }

  try {
    const config = getRemoteConfigInstance();
    const activated = await fetchAndActivate(config);

    isInitialized = true;

    logger.info('FeatureFlags', 'Remote Config fetched and activated', {
      activated,
      fetchTime: new Date().toISOString(),
    });

    return true;
  } catch (error) {
    // Don't fail the app if Remote Config fails - use default values
    logger.warn(
      'FeatureFlags',
      'Failed to fetch Remote Config, using defaults',
      {
        error: error instanceof Error ? error.message : String(error),
      }
    );

    isInitialized = true; // Mark as initialized to prevent repeated attempts
    return false;
  }
};

// Purpose: Check if a feature flag is enabled.
// Returns the boolean value of the flag, or false if not set.
export const isFeatureEnabled = (flagName: string): boolean => {
  try {
    const config = getRemoteConfigInstance();
    const value = getValue(config, flagName);

    logger.debug('FeatureFlags', `Checking feature flag: ${flagName}`, {
      value: value.asBoolean(),
      source: value.getSource(),
    });

    return value.asBoolean();
  } catch (error) {
    logger.error('FeatureFlags', `Error checking feature flag: ${flagName}`, {
      error: error instanceof Error ? error.message : String(error),
    });
    return false;
  }
};

// Purpose: Get the string value of a feature flag.
// Useful for flags that contain configuration values (e.g., API endpoints, thresholds).
export const getFeatureValue = (flagName: string): string => {
  try {
    const config = getRemoteConfigInstance();
    const value = getValue(config, flagName);

    logger.debug('FeatureFlags', `Getting feature value: ${flagName}`, {
      value: value.asString(),
      source: value.getSource(),
    });

    return value.asString();
  } catch (error) {
    logger.error('FeatureFlags', `Error getting feature value: ${flagName}`, {
      error: error instanceof Error ? error.message : String(error),
    });
    return '';
  }
};

// Purpose: Get the numeric value of a feature flag.
// Useful for flags that contain numeric configuration (e.g., rollout percentages, limits).
export const getFeatureNumber = (flagName: string): number => {
  try {
    const config = getRemoteConfigInstance();
    const value = getValue(config, flagName);

    logger.debug('FeatureFlags', `Getting feature number: ${flagName}`, {
      value: value.asNumber(),
      source: value.getSource(),
    });

    return value.asNumber();
  } catch (error) {
    logger.error('FeatureFlags', `Error getting feature number: ${flagName}`, {
      error: error instanceof Error ? error.message : String(error),
    });
    return 0;
  }
};

// Purpose: Check if feature flags have been initialized.
export const isFeatureFlagsInitialized = (): boolean => {
  return isInitialized;
};
